/* File : src/services/indexedDB.js */
import Dexie from 'dexie';

// Création de la base de données IndexedDB avec Dexie
const db = new Dexie('GuinnessBrightBar');

// Définition du schéma de la base de données
db.version(1).stores({
  users: 'id, phone, name, isValidated',
  tickets: 'id, userId, ticketCode, qrData',
  entries: '++id, ticketId, scanTime, isOffline, deviceId',
  offlineQueue: '++id, action, data, timestamp'
});

// Service pour interagir avec IndexedDB
const indexedDBService = {
  // Initialisation de la base de données
  async initializeDB() {
    try {
      await db.open();
      console.log('IndexedDB initialized successfully');
      return true;
    } catch (error) {
      console.error('Error initializing IndexedDB:', error);
      return false;
    }
  },

  // Services pour les utilisateurs
  users: {
    async add(user) {
      try {
        const id = await db.users.add(user);
        return id;
      } catch (error) {
        console.error('Error adding user to IndexedDB:', error);
        throw error;
      }
    },

    async bulkAdd(users) {
      try {
        await db.users.bulkAdd(users);
        return true;
      } catch (error) {
        console.error('Error bulk adding users to IndexedDB:', error);
        throw error;
      }
    },

    async getByPhone(phone) {
      try {
        return await db.users.where('phone').equals(phone).first();
      } catch (error) {
        console.error('Error getting user by phone from IndexedDB:', error);
        throw error;
      }
    },

    async getById(id) {
      try {
        return await db.users.get(id);
      } catch (error) {
        console.error('Error getting user by ID from IndexedDB:', error);
        throw error;
      }
    },

    async getAll() {
      try {
        return await db.users.toArray();
      } catch (error) {
        console.error('Error getting all users from IndexedDB:', error);
        throw error;
      }
    },

    async update(id, changes) {
      try {
        await db.users.update(id, changes);
        return true;
      } catch (error) {
        console.error('Error updating user in IndexedDB:', error);
        throw error;
      }
    }
  },

  // Services pour les tickets
  tickets: {
    async add(ticket) {
      try {
        const id = await db.tickets.add(ticket);
        return id;
      } catch (error) {
        console.error('Error adding ticket to IndexedDB:', error);
        throw error;
      }
    },

    async getByUserId(userId) {
      try {
        return await db.tickets.where('userId').equals(userId).first();
      } catch (error) {
        console.error('Error getting ticket by userId from IndexedDB:', error);
        throw error;
      }
    },

    async getByQRData(qrData) {
      try {
        return await db.tickets.where('qrData').equals(qrData).first();
      } catch (error) {
        console.error('Error getting ticket by QR data from IndexedDB:', error);
        throw error;
      }
    },

    async getAll() {
      try {
        return await db.tickets.toArray();
      } catch (error) {
        console.error('Error getting all tickets from IndexedDB:', error);
        throw error;
      }
    }
  },

  // Services pour les entrées (scans)
  entries: {
    async add(entry) {
      try {
        // Ajouter l'heure de scan si non spécifiée
        if (!entry.scanTime) {
          entry.scanTime = new Date().toISOString();
        }
        
        const id = await db.entries.add(entry);
        return id;
      } catch (error) {
        console.error('Error adding entry to IndexedDB:', error);
        throw error;
      }
    },

    async getByTicketId(ticketId) {
      try {
        return await db.entries.where('ticketId').equals(ticketId).toArray();
      } catch (error) {
        console.error('Error getting entries by ticket ID from IndexedDB:', error);
        throw error;
      }
    },

    async getOfflineEntries() {
      try {
        return await db.entries.where('isOffline').equals(1).toArray();
      } catch (error) {
        console.error('Error getting offline entries from IndexedDB:', error);
        throw error;
      }
    },

    async markAsSynced(id) {
      try {
        await db.entries.update(id, { isOffline: 0 });
        return true;
      } catch (error) {
        console.error('Error marking entry as synced in IndexedDB:', error);
        throw error;
      }
    },

    async getAll() {
      try {
        return await db.entries.toArray();
      } catch (error) {
        console.error('Error getting all entries from IndexedDB:', error);
        throw error;
      }
    }
  },

  // File d'attente pour les opérations hors ligne
  offlineQueue: {
    async add(action, data) {
      try {
        const queueItem = {
          action,
          data,
          timestamp: new Date().toISOString()
        };
        
        const id = await db.offlineQueue.add(queueItem);
        return id;
      } catch (error) {
        console.error('Error adding item to offline queue in IndexedDB:', error);
        throw error;
      }
    },

    async getAll() {
      try {
        return await db.offlineQueue.toArray();
      } catch (error) {
        console.error('Error getting all items from offline queue in IndexedDB:', error);
        throw error;
      }
    },

    async remove(id) {
      try {
        await db.offlineQueue.delete(id);
        return true;
      } catch (error) {
        console.error('Error removing item from offline queue in IndexedDB:', error);
        throw error;
      }
    },

    async clear() {
      try {
        await db.offlineQueue.clear();
        return true;
      } catch (error) {
        console.error('Error clearing offline queue in IndexedDB:', error);
        throw error;
      }
    }
  },

  // Méthode pour synchroniser les données hors ligne avec le serveur
  async syncOfflineData(syncFunction) {
    try {
      // Récupérer les éléments en file d'attente
      const queueItems = await this.offlineQueue.getAll();
      
      if (queueItems.length === 0) {
        return { success: true, message: 'No items to sync' };
      }
      
      // Appeler la fonction de synchronisation externe avec les données
      const result = await syncFunction(queueItems);
      
      // Si la synchronisation a réussi, vider la file d'attente
      if (result.success) {
        await this.offlineQueue.clear();
      }
      
      return result;
    } catch (error) {
      console.error('Error syncing offline data:', error);
      return { 
        success: false, 
        message: 'Error during synchronization', 
        error 
      };
    }
  }
};

export default indexedDBService;